/**
 * One extraction per mail, outliving whatever component started it.
 *
 * ---- Why this exists ----
 *
 * `useExtractFromMail` used to own a `useReducer`, so a run's state died with the component. That
 * blocks pre-warming outright: a run started when the attachment preview opens would be discarded
 * the moment that modal closes, and `CreateEnquiryBar` — which mounts later — would see nothing.
 *
 * It also fixes a live bug. Nothing prevented two concurrent runs for one mail; the only guard used
 * to be a disabled button on the extraction trigger, which a remount (e.g. re-opening the mail)
 * would reset. A second `startRun` for the same mail now JOINS the first instead.
 *
 * ---- What it does not do ----
 *
 * It holds no extraction logic. `extractionReducer` (`mailExtractionState.ts`) remains the only
 * thing that decides a phase transition — this module just keeps the result of that decision
 * somewhere a second component can read it. Entries hold a phase and a result, never blobs, so
 * there is no eviction policy beyond `resetRun`.
 *
 * ---- Safety invariant: `resetRun` must only be called from a terminal phase ----
 *
 * `resetRun` must never be called while a run is in flight (`isRunning(mailId)` true — phase
 * `submitting`/`pending`/`falling-back`). Actions dispatched by `apply` carry no run identity —
 * nothing on an `ExtractionAction` says which `startRun` call produced it — so a reset that lands
 * mid-flight does not stop the abandoned run; it only puts a fresh `IDLE_PHASE` in the entry for
 * the OLD run's `dispatch` closure to keep writing into. The old run's late `apply` calls then
 * replay on top of whatever run started after the reset, landing a stale dispatch on a newer run's
 * phase — e.g. an old run's `succeeded` overwriting a new run's `pending`. Every caller of
 * `resetRun` in this codebase (see `useMailExtraction.ts`'s `mutateAndDeliver` and `reset`) must
 * only do so once the entry's phase is terminal (`idle`/`succeeded`/`failed`) — never from inside
 * an in-flight run's own callback chain.
 */

import { extractionReducer, IDLE_PHASE, type ExtractionAction, type ExtractionPhase } from './mailExtractionState'

interface Entry {
  phase: ExtractionPhase
  /** In flight when set. Joined by a second caller rather than duplicated. */
  promise?: Promise<void>
  listeners: Set<() => void>
}

const entries = new Map<string, Entry>()

function entryFor(mailId: string): Entry {
  const existing = entries.get(mailId)
  if (existing) return existing
  const created: Entry = { phase: IDLE_PHASE, listeners: new Set() }
  entries.set(mailId, created)
  return created
}

/** Stable across calls while nothing changed — `useSyncExternalStore` compares by reference, and
 * a fresh object every read would spin the renderer. `extractionReducer` already preserves the
 * reference for a no-op transition, which is what makes this hold. */
export function getPhase(mailId: string): ExtractionPhase {
  return entries.get(mailId)?.phase ?? IDLE_PHASE
}

export function isRunning(mailId: string): boolean {
  return entries.get(mailId)?.promise !== undefined
}

export function subscribe(mailId: string, listener: () => void): () => void {
  const entry = entryFor(mailId)
  entry.listeners.add(listener)
  return () => {
    entry.listeners.delete(listener)
  }
}

function apply(mailId: string, action: ExtractionAction): void {
  const entry = entryFor(mailId)
  const next = extractionReducer(entry.phase, action)
  // A no-op transition returns the SAME reference — do not notify for it.
  if (next === entry.phase) return
  entry.phase = next
  for (const listener of entry.listeners) listener()
}

/**
 * Run `run` for `mailId`, or join the run already going. Resolves when the run settles.
 *
 * A mail that has already SUCCEEDED does not re-run — that result is the thing pre-warming exists
 * to produce. A failed one may be retried, because the user asking again is a deliberate retry.
 *
 * No third `hooks`/`onAction` parameter — an earlier version had one, and it caused a Critical: a
 * `startRun` call that JOINS a run already in flight returns the existing run's promise before
 * ever looking at its own `hooks` (see the `if (entry.promise) return entry.promise` line above),
 * so a caller relying on `onAction` for its own callbacks silently got none. The fix was to make
 * `startRun`'s returned promise itself the only channel — read the RESULT after it settles
 * (`getPhase(mailId)`, see `useMailExtraction.ts`'s `runAndDeliver`) rather than tapping the
 * dispatch stream. Do not re-add a side-channel here; it is the exact shape of bug that bit before.
 */
export function startRun(
  mailId: string,
  run: (dispatch: (action: ExtractionAction) => void) => Promise<void>,
): Promise<void> {
  const entry = entryFor(mailId)
  if (entry.promise) return entry.promise
  if (entry.phase.status === 'succeeded') return Promise.resolve()

  const promise = run((action) => apply(mailId, action))
    .catch(() => {
      // `runExtraction` reports its own failures through dispatch; a throw here would otherwise
      // leave the entry stuck "running" forever.
    })
    .finally(() => {
      const current = entries.get(mailId)
      if (current && current.promise === promise) current.promise = undefined
    })

  entry.promise = promise
  return promise
}

/** Back to idle, so the next `startRun` really runs. */
export function resetRun(mailId: string): void {
  const entry = entries.get(mailId)
  if (!entry) return
  entry.phase = IDLE_PHASE
  entry.promise = undefined
  for (const listener of entry.listeners) listener()
}

/** Tests only — module state persists across cases otherwise. */
export function __resetStoreForTests(): void {
  entries.clear()
}
